import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useForm } from "react-hook-form";
import axios from "axios";
import Cookies from "universal-cookie";
import Swal from "sweetalert2";

import convierte from "../adaptadores/convierte.js";
import seteaCokie from "../tools/seteaCokie.js";
import ObtieneDatosCookies from "../tools/obtieneDatosCookiesUsuario.js";
import asignaPass from "../tools/asignaPass.js";
import devuelveSedeConsultada from "../tools/devuelveSede";
import devuelveDependenciaConstultada from "../tools/devuelveDependencia.js";
import socket from "../tools/socke.js";

import Permisos from "./Permisos.jsx";

// ============================================================
// HELPERS
// ============================================================

const convertirPermisosArray = (valor) => {
  if (!valor) {
    return [];
  }

  if (Array.isArray(valor)) {
    return [
      ...new Set(
        valor
          .map((item) =>
            String(item).trim()
          )
          .filter(Boolean)
      ),
    ];
  }

  return [
    ...new Set(
      String(valor)
        .split(",")
        .map((item) =>
          item.trim()
        )
        .filter(Boolean)
    ),
  ];
};

const permisosAString = (valor) => {
  return convertirPermisosArray(
    valor
  ).join(",");
};

// ============================================================
// COMPONENTE
// ============================================================

function ModuloEditaUsuarios(props) {
  const cookies =
    new Cookies();

  const parametro =
    props.datos || {};

  const destinosLocales = devuelveDependenciaConstultada() || [];
  const [destinosServidor, setDestinosServidor] = useState(null);
  const [cargandoDestinos, setCargandoDestinos] = useState(true);

  // Misma fuente jerárquica que ModuloUsuarios.
  useEffect(() => {
    let activo = true;
    const base = (process.env.REACT_APP_BASEURL || "/expedientes/").replace(/\/?$/, "/");
    const token = localStorage.getItem("tokenJWT");
    const config = {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    };
    const aplanar = (nodos, padre = null) =>
      nodos.flatMap((n) => [
        { ...n, dependenciaPadreId: n.dependenciaPadreId ?? n.dependencia_padre_id ?? n.dependeDe ?? padre },
        ...aplanar(n.hijos || [], n.id),
      ]);
    (async () => {
      try {
        let registros;
        try {
          const { data } = await axios.post(`${base}dependencias`, {}, config);
          registros = Array.isArray(data) ? data : data?.data;
          if (!Array.isArray(registros)) throw new Error("Respuesta de dependencias inválida");
        } catch (_) {
          const { data } = await axios.post(`${base}dependencias/arbol`, {}, config);
          if (!data?.ok || !Array.isArray(data.data)) throw new Error("No se pudo cargar el organigrama");
          registros = aplanar(data.data);
        }
        if (activo) setDestinosServidor(registros);
      } catch (error) {
        console.error("No se pudo cargar el organigrama para editar usuarios:", error);
        if (activo) setDestinosServidor([]); // Falla cerrada: no habilitar cambios de destino.
      } finally {
        if (activo) setCargandoDestinos(false);
      }
    })();
    return () => { activo = false; };
  }, []);

  const destinos = destinosServidor ?? destinosLocales;

  const sedes =
    devuelveSedeConsultada() ||
    [];

  const Usuario =
    ObtieneDatosCookies();

  const URI =
    "https://wwwa.isepsantafe.net.ar/expedientes/usuarios/actualiza/";

  // ==========================================================
  // DATOS DEL ADMINISTRADOR LOGUEADO
  // ==========================================================

  const nivelAdministrador =
    Number(
      Usuario?.nivel
    );

  const destinoAdministrador =
    String(
      Usuario?.destino ?? ""
    );

  const tienePermisoCambioGlobal = convertirPermisosArray(Usuario?.funcion)
    .some(p => p.toLowerCase() === "cambio los destinos de todos");

  const esSuperUsuario =
    nivelAdministrador === 1;

  const esAdministrador =
    nivelAdministrador === 2;

  const puedeGestionarUsuarios =
    esSuperUsuario ||
    esAdministrador ||
    tienePermisoCambioGlobal;

  // ==========================================================
  // ESTADOS
  // ==========================================================

  const [
    permisos,
    setPermisos,
  ] = useState(
    parametro.funcion || ""
  );

  const [
    guardando,
    setGuardando,
  ] = useState(false);

  const [
    blanqueando,
    setBlanqueando,
  ] = useState(false);

  const [
    cerrandoSesion,
    setCerrandoSesion,
  ] = useState(false);

  const [
    mostrarPermisos,
    setMostrarPermisos,
  ] = useState(false);

  // ==========================================================
  // FORM
  // ==========================================================

  const {
    register,

    formState: {
      errors,
      isDirty,
    },

    handleSubmit,
    reset,
    watch,
  } = useForm({
    defaultValues:
      parametro,
  });

  // ==========================================================
  // AL CAMBIAR USUARIO
  // ==========================================================

  useEffect(() => {
    reset(parametro);

    setPermisos(
      parametro.funcion || ""
    );

    setMostrarPermisos(
      false
    );
  }, [
    parametro,
    reset,
  ]);

  // ==========================================================
  // NOMBRE
  // ==========================================================

  const nombreCompleto =
    useMemo(() => {
      return `${parametro.apellido || ""} ${
        parametro.nombre || ""
      }`.trim();
    }, [
      parametro.apellido,
      parametro.nombre,
    ]);

  // ==========================================================
  // OBTENER PADRE DE UNA DEPENDENCIA
  //
  // SOPORTAMOS DISTINTOS NOMBRES DEL BACK
  // ==========================================================

  const obtenerPadreId = (
    dependencia
  ) => {
    return (
      dependencia
        ?.dependencia_padre_id ??
      dependencia
        ?.dependenciaPadreId ??
      dependencia
        ?.dependenciaPadreID ??
      dependencia?.dependencia_padreId ??
      dependencia?.padre_id ??
      dependencia?.padreId ??
      dependencia?.dependeDe ??
      dependencia?.padre?.id ??
      null
    );
  };

  // ==========================================================
  // OBTENER SEDE DE UNA DEPENDENCIA
  // ==========================================================

  const obtenerSedeId = (
    dependencia
  ) => {
    return (
      dependencia?.sedeID ??
      dependencia?.sedeId ??
      dependencia?.sede_id ??
      null
    );
  };

  // ==========================================================
  // OBTENER TODA LA RAMA DESCENDIENTE
  //
  // INCLUYE:
  //
  // - DEPENDENCIA INICIAL
  // - HIJOS
  // - NIETOS
  // - TODOS LOS DESCENDIENTES
  // ==========================================================

  const obtenerDependenciasDescendientes =
    (
      dependenciaInicialId
    ) => {
      if (
        dependenciaInicialId ===
          undefined ||
        dependenciaInicialId ===
          null ||
        dependenciaInicialId ===
          ""
      ) {
        return [];
      }

      const resultado =
        new Set();

      const recorrer = (
        dependenciaId
      ) => {
        const idActual =
          String(
            dependenciaId
          );

        if (
          resultado.has(
            idActual
          )
        ) {
          return;
        }

        resultado.add(
          idActual
        );

        const hijos =
          destinos.filter(
            (
              dependencia
            ) =>
              String(
                obtenerPadreId(
                  dependencia
                )
              ) ===
              idActual
          );

        hijos.forEach(
          (hijo) => {
            recorrer(
              hijo.id
            );
          }
        );
      };

      recorrer(
        dependenciaInicialId
      );

      return Array.from(
        resultado
      );
    };

  // ==========================================================
  // DESTINOS QUE PUEDE ADMINISTRAR
  // ==========================================================

  const destinosPermitidos = useMemo(() => {
    if (!destinosServidor || !destinos.length) return [];
    const todos = (esSuperUsuario || tienePermisoCambioGlobal)
      ? destinos.map((d) => String(d.id))
      : esAdministrador
        ? obtenerDependenciasDescendientes(destinoAdministrador)
        : [];
    // La rama reservada solo puede ser gestionada por administradores de esa rama.
    if (destinoAdministrador === "7" && (esSuperUsuario || esAdministrador)) return todos;
    const reservados = new Set(obtenerDependenciasDescendientes("7"));
    reservados.add("7");
    return todos.filter((id) => !reservados.has(String(id)));
  }, [destinos, destinosServidor, esSuperUsuario, esAdministrador, destinoAdministrador, tienePermisoCambioGlobal]);

  // ==========================================================
  // DESTINOS DISPONIBLES EN EL SELECT
  // ==========================================================

  const destinosEditables = useMemo(() =>
    destinos.filter((d) => destinosPermitidos.includes(String(d.id))),
    [destinos, destinosPermitidos]
  );

  // ==========================================================
  // SEDES QUE PUEDE UTILIZAR
  //
  // SUPER USER:
  // TODAS
  //
  // ADMIN:
  // LAS QUE ESTÉN ASOCIADAS A SU RAMA.
  //
  // SI TODAVÍA EL BACK NO MANDA sedeID EN DEPENDENCIAS,
  // CONSERVAMOS LA SEDE ACTUAL PARA NO ROMPER EL FORM.
  // ==========================================================

  const sedesPermitidas =
    useMemo(() => {
      if (
        esSuperUsuario
      ) {
        return sedes;
      }

      if (
        !esAdministrador
      ) {
        return [];
      }

      const idsSedes =
        new Set();

      destinosEditables.forEach(
        (
          dependencia
        ) => {
          const sedeId =
            obtenerSedeId(
              dependencia
            );

          if (
            sedeId !==
              null &&
            sedeId !==
              undefined &&
            sedeId !==
              ""
          ) {
            idsSedes.add(
              String(
                sedeId
              )
            );
          }
        }
      );

      // ======================================================
      // TODAVÍA NO TENEMOS SEDE ASOCIADA EN LAS DEPENDENCIAS
      //
      // PARA NO ROMPER EL COMPONENTE:
      // DEJAMOS COMO MÍNIMO LA SEDE ACTUAL DEL USUARIO
      // ======================================================

      if (
        idsSedes.size ===
          0 &&
        parametro.sedeID !==
          undefined &&
        parametro.sedeID !==
          null &&
        parametro.sedeID !==
          ""
      ) {
        idsSedes.add(
          String(
            parametro.sedeID
          )
        );
      }

      return sedes.filter(
        (sede) =>
          idsSedes.has(
            String(
              sede.id
            )
          )
      );
    }, [
      esSuperUsuario,
      esAdministrador,
      sedes,
      destinosEditables,
      parametro.sedeID,
    ]);

  // ==========================================================
  // EL USUARIO EDITADO ESTÁ DENTRO DE MI ÁMBITO?
  //
  // SUPER USER:
  // CUALQUIERA
  //
  // ADMIN:
  // - DESTINO PROPIO O DESCENDIENTE
  // - SOLO NIVEL 3 O 4
  // ==========================================================

  const usuarioDentroDelAmbito = useMemo(() => {
    if (!destinosServidor) return false;
    const destinoPermitido = destinosPermitidos.includes(String(parametro.destino));
    if (!destinoPermitido) return false;
    if (esSuperUsuario || tienePermisoCambioGlobal) return true;
    return esAdministrador && Number(parametro.nivel) >= 3;
  }, [destinosServidor, destinosPermitidos, parametro.destino, parametro.nivel, esSuperUsuario, esAdministrador, tienePermisoCambioGlobal]);

  // ==========================================================
  // PERMISOS DEL ADMINISTRADOR
  //
  // SUPER USER = NULL => TODOS
  //
  // ADMIN = SOLO SUS PROPIOS PERMISOS
  // ==========================================================

  const permisosAdministrador =
    useMemo(() => {
      if (
        esSuperUsuario
      ) {
        return null;
      }

      if (
        esAdministrador
      ) {
        return convertirPermisosArray(
          Usuario?.funcion
        );
      }

      return [];
    }, [
      esSuperUsuario,
      esAdministrador,
      Usuario?.funcion,
    ]);

  // ==========================================================
  // PERMISOS ORIGINALES DEL USUARIO EDITADO
  // ==========================================================

  const permisosOriginalesUsuario =
    useMemo(() => {
      return convertirPermisosArray(
        parametro.funcion
      );
    }, [
      parametro.funcion,
    ]);

  // ==========================================================
  // PERMISOS PROTEGIDOS
  //
  // SI EL USUARIO TIENE ALGÚN PERMISO QUE EL ADMIN
  // NO POSEE, NO SE LO MOSTRAMOS COMO EDITABLE
  // PERO TAMPOCO LO BORRAMOS AL GUARDAR.
  // ==========================================================

  const permisosProtegidos =
    useMemo(() => {
      if (
        esSuperUsuario
      ) {
        return [];
      }

      if (
        !esAdministrador
      ) {
        return permisosOriginalesUsuario;
      }

      return permisosOriginalesUsuario.filter(
        (permiso) =>
          !permisosAdministrador.includes(
            permiso
          )
      );
    }, [
      esSuperUsuario,
      esAdministrador,
      permisosOriginalesUsuario,
      permisosAdministrador,
    ]);

  // ==========================================================
  // WATCH
  // ==========================================================

  const destinoSeleccionado =
    watch("destino");

  const sedeSeleccionada =
    watch("sedeID");

  const estadoSeleccionado =
    watch("estado");

  const nivelSeleccionado =
    watch("nivel");

  // ==========================================================
  // ESTADO ACTUAL
  // ==========================================================

  const estadoActual =
    String(
      estadoSeleccionado
    ) === "1"
      ? "Activo"
      : "Suspendido";

  // ==========================================================
  // NIVEL ACTUAL
  // ==========================================================

  const nivelActual =
    String(
      nivelSeleccionado ||
        ""
    );

  // ==========================================================
  // DESTINO ACTUAL
  // ==========================================================

  const destinoActual =
    useMemo(() => {
      return destinos.find(
        (item) =>
          String(
            item.id
          ) ===
          String(
            destinoSeleccionado ||
              parametro.destino
          )
      );
    }, [
      destinos,
      destinoSeleccionado,
      parametro.destino,
    ]);

  // ==========================================================
  // SEDE ACTUAL
  // ==========================================================

  const sedeActual =
    useMemo(() => {
      return sedes.find(
        (item) =>
          String(
            item.id
          ) ===
          String(
            sedeSeleccionada ||
              parametro.sedeID
          )
      );
    }, [
      sedes,
      sedeSeleccionada,
      parametro.sedeID,
    ]);

  // ==========================================================
  // CAMBIO DE PERMISOS
  // ==========================================================

  const cambiarPermisos = (
    nuevosPermisos
  ) => {
    // ========================================================
    // SUPER USER
    // ========================================================

    if (
      esSuperUsuario
    ) {
      setPermisos(
        permisosAString(
          nuevosPermisos
        )
      );

      return;
    }

    // ========================================================
    // ADMINISTRADOR
    //
    // FILTRAMOS TODO LO QUE NO TENGA ÉL MISMO
    // ========================================================

    if (
      esAdministrador
    ) {
      const solicitados =
        convertirPermisosArray(
          nuevosPermisos
        );

      const permitidos =
        solicitados.filter(
          (permiso) =>
            permisosAdministrador.includes(
              permiso
            )
        );

      setPermisos(
        permisosAString(
          permitidos
        )
      );
    }
  };

  // ==========================================================
  // CONSTRUIR PERMISOS FINALES
  // ==========================================================

  const obtenerPermisosFinales =
    () => {
      // ======================================================
      // SUPER USER
      // ======================================================

      if (
        esSuperUsuario
      ) {
        return permisosAString(
          permisos
        );
      }

      // ======================================================
      // ADMINISTRADOR NIVEL 2
      // ======================================================

      if (
        esAdministrador
      ) {
        const editables =
          convertirPermisosArray(
            permisos
          ).filter(
            (permiso) =>
              permisosAdministrador.includes(
                permiso
              )
          );

        return permisosAString([
          ...permisosProtegidos,
          ...editables,
        ]);
      }

      return permisosAString(
        parametro.funcion
      );
    };

  // ==========================================================
  // VALIDAR DESTINO
  // ==========================================================

  const validarDestino = (destino) =>
    destinosPermitidos.includes(String(destino));

  // ==========================================================
  // VALIDAR SEDE
  // ==========================================================

  const validarSede = (
    sedeID
  ) => {
    if (
      esSuperUsuario || tienePermisoCambioGlobal
    ) {
      return true;
    }

    if (
      !esAdministrador
    ) {
      return false;
    }

    return sedesPermitidas.some(
      (sede) =>
        String(
          sede.id
        ) ===
        String(
          sedeID
        )
    );
  };

  // ==========================================================
  // ACTUALIZAR
  // ==========================================================

  const update = async (
    data
  ) => {
    try {
      setGuardando(true);

      // ======================================================
      // VALIDAR QUE PUEDA TOCAR ESTE USUARIO
      // ======================================================

      if (
        cargandoDestinos || !usuarioDentroDelAmbito
      ) {
        await Swal.fire({
          icon:
            "error",

          title:
            "Usuario fuera de su ámbito",

          text:
            "No tiene autorización para administrar este usuario.",
        });

        return;
      }

      // ======================================================
      // VALIDAR DESTINO
      // ======================================================

      if (
        !validarDestino(
          data.destino
        )
      ) {
        await Swal.fire({
          icon:
            "error",

          title:
            "Destino no permitido",

          text:
            "No puede mover al usuario fuera de las dependencias que administra.",
        });

        return;
      }

      // ======================================================
      // VALIDAR SEDE
      // ======================================================

      if (
        !validarSede(
          data.sedeID
        )
      ) {
        await Swal.fire({
          icon:
            "error",

          title:
            "Sede no permitida",

          text:
            "No puede asignar al usuario a una sede fuera de su ámbito administrativo.",
        });

        return;
      }

      // ======================================================
      // VALIDAR NIVEL
      //
      // ADMIN NIVEL 2 NO PUEDE ASIGNAR:
      //
      // NIVEL 1
      // NIVEL 2
      // ======================================================

      if (
        esAdministrador &&
        Number(
          data.nivel
        ) < 3
      ) {
        await Swal.fire({
          icon:
            "error",

          title:
            "Nivel no permitido",

          text:
            "Un administrador solamente puede asignar niveles Operador u Observador.",
        });

        return;
      }

      // ======================================================
      // PERMISOS DEFINITIVOS
      // ======================================================

      const permisosFinales =
        obtenerPermisosFinales();

      // El permiso especial autoriza traslados, no cambios de nivel, permisos
      // ni de otros datos personales. Se conserva el registro original.
      const soloTrasladoGlobal = tienePermisoCambioGlobal && !esSuperUsuario;
      const datosEnviar = soloTrasladoGlobal
        ? { ...parametro, destino: data.destino, funcion: parametro.funcion }
        : { ...data, funcion: permisosFinales };

      console.log(
        "========================================"
      );

      console.log(
        "USUARIO LOGUEADO:",
        Usuario
      );

      console.log(
        "USUARIO EDITADO:",
        parametro
      );

      console.log(
        "DESTINOS PERMITIDOS:",
        destinosPermitidos
      );

      console.log(
        "DESTINOS EDITABLES:",
        destinosEditables
      );

      console.log(
        "SEDES PERMITIDAS:",
        sedesPermitidas
      );

      console.log(
        "PERMISOS ADMIN:",
        permisosAdministrador
      );

      console.log(
        "PERMISOS PROTEGIDOS:",
        permisosProtegidos
      );

      console.log(
        "DATOS A ENVIAR:",
        datosEnviar
      );

      console.log(
        "========================================"
      );

      // ======================================================
      // UPDATE
      // ======================================================

      await axios.put(
        URI +
          parametro.identificacion,

        convierte(
          datosEnviar
        )
      );

      // ======================================================
      // SI EDITÓ SU PROPIO USUARIO
      // ======================================================

      if (
        String(
          cookies.get(
            "id",
            {
              path: "/",
            }
          )
        ) ===
        String(
          data.id
        )
      ) {
        seteaCokie(
          datosEnviar
        );
      }

      await Swal.fire({
        icon:
          "success",

        title:
          "Usuario actualizado",

        text:
          "Los cambios fueron guardados correctamente.",

        timer:
          1600,

        showConfirmButton:
          false,
      });

      if (
        typeof props.myID ===
        "function"
      ) {
        props.myID(
          datosEnviar
        );
      }
    } catch (error) {
      console.error(
        "Error actualizando usuario:",
        error
      );

      await Swal.fire({
        icon:
          "error",

        title:
          "No se pudo actualizar",

        text:
          error?.response
            ?.data
            ?.message ||
          error?.response
            ?.data
            ?.error ||
          error?.message ||
          "Ocurrió un error al actualizar el usuario.",
      });
    } finally {
      setGuardando(
        false
      );
    }
  };

  // ==========================================================
  // GUARDAR
  // ==========================================================

  const guardar = async (
    data
  ) => {
    const destino =
      destinos.find(
        (item) =>
          String(
            item.id
          ) ===
          String(
            data.destino
          )
      );

    const sede =
      sedes.find(
        (item) =>
          String(
            item.id
          ) ===
          String(
            data.sedeID
          )
      );

    const confirmacion =
      await Swal.fire({
        title:
          "¿Guardar cambios?",

        html: `
          <div style="text-align:left">
            Se actualizará el usuario:
            <br/><br/>

            <strong>
              ${data.apellido || ""}
              ${data.nombre || ""}
            </strong>

            <br/>

            Identificación:
            <strong>
              ${
                parametro.identificacion ||
                ""
              }
            </strong>

            <br/><br/>

            Destino:
            <strong>
              ${
                destino?.descripcion ||
                data.destino
              }
            </strong>

            <br/>

            Sede:
            <strong>
              ${
                sede?.descripcion ||
                data.sedeID
              }
            </strong>

            <br/>

            Nivel:
            <strong>
              ${nombreNivel(
                data.nivel
              )}
            </strong>
          </div>
        `,

        icon:
          "question",

        showCancelButton:
          true,

        confirmButtonText:
          "Sí, guardar",

        cancelButtonText:
          "Cancelar",

        reverseButtons:
          true,
      });

    if (
      !confirmacion.isConfirmed
    ) {
      return;
    }

    await update(
      data
    );
  };

  // ==========================================================
  // BLANQUEAR CONTRASEÑA
  // ==========================================================

  const blanquear =
    async () => {
      if (
        !usuarioDentroDelAmbito
      ) {
        return Swal.fire({
          icon:
            "error",

          title:
            "Acceso denegado",

          text:
            "El usuario se encuentra fuera de su ámbito administrativo.",
        });
      }

      const confirmacion =
        await Swal.fire({
          title:
            "¿Blanquear contraseña?",

          html: `
            <div style="text-align:left">
              Se asignará una nueva contraseña a:
              <br/><br/>

              <strong>
                ${nombreCompleto}
              </strong>

              <br/><br/>

              Nueva contraseña:
              <strong>
                Alem+2050
              </strong>
            </div>
          `,

          icon:
            "warning",

          showCancelButton:
            true,

          confirmButtonText:
            "Sí, blanquear",

          cancelButtonText:
            "Cancelar",

          reverseButtons:
            true,
        });

      if (
        !confirmacion.isConfirmed
      ) {
        return;
      }

      try {
        setBlanqueando(
          true
        );

        const nuevoPass =
          await asignaPass(
            "Alem+2050"
          );

        const pass =
          nuevoPass.pass;

        await axios.put(
          URI +
            parametro.identificacion,

          {
            pass,

            blanqueoContrasena:
              "1",
          }
        );

        await Swal.fire({
          icon:
            "success",

          title:
            "Contraseña blanqueada",

          html: `
            <div>
              Nueva contraseña:
              <br/><br/>

              <strong style="font-size:22px">
                Alem+2050
              </strong>
            </div>
          `,

          confirmButtonText:
            "Aceptar",
        });
      } catch (error) {
        console.error(
          "Error blanqueando contraseña:",
          error
        );

        await Swal.fire({
          icon:
            "error",

          title:
            "Error",

          text:
            error?.response
              ?.data
              ?.message ||
            error?.message ||
            "No se pudo blanquear la contraseña.",
        });
      } finally {
        setBlanqueando(
          false
        );
      }
    };

  // ==========================================================
  // CERRAR SESIÓN
  // ==========================================================

  const CierraSesion =
    async () => {
      if (
        !usuarioDentroDelAmbito
      ) {
        return Swal.fire({
          icon:
            "error",

          title:
            "Acceso denegado",

          text:
            "El usuario se encuentra fuera de su ámbito administrativo.",
        });
      }

      const confirmacion =
        await Swal.fire({
          title:
            "¿Cerrar sesión?",

          html: `
            <div>
              Se cerrará la sesión activa de
              <br/>

              <strong>
                ${nombreCompleto}
              </strong>

              <br/>

              <span style="color:#64748b">
                ${
                  parametro.identificacion ||
                  ""
                }
              </span>
            </div>
          `,

          icon:
            "warning",

          showCancelButton:
            true,

          confirmButtonText:
            "Sí, cerrar sesión",

          cancelButtonText:
            "Cancelar",

          reverseButtons:
            true,
        });

      if (
        !confirmacion.isConfirmed
      ) {
        return;
      }

      try {
        setCerrandoSesion(
          true
        );

        socket.emit(
          "cierraUsuario",
          parametro.identificacion
        );

        await Swal.fire({
          icon:
            "success",

          title:
            "Sesión cerrada",

          text:
            `Se solicitó el cierre de sesión del usuario ${parametro.identificacion}.`,

          timer:
            1800,

          showConfirmButton:
            false,
        });
      } catch (error) {
        console.error(
          "Error cerrando sesión:",
          error
        );

        Swal.fire({
          icon:
            "error",

          title:
            "Error",

          text:
            "No se pudo solicitar el cierre de sesión.",
        });
      } finally {
        setCerrandoSesion(
          false
        );
      }
    };

  // ==========================================================
  // LABEL NIVEL
  // ==========================================================

  const nombreNivel = (
    nivel
  ) => {
    switch (
      String(
        nivel
      )
    ) {
      case "1":
        return "Super User";

      case "2":
        return "Administrador";

      case "3":
        return "Operador";

      case "4":
        return "Observador";

      default:
        return `Nivel ${nivel}`;
    }
  };

  // ==========================================================
  // ESTILOS
  // ==========================================================

  const estiloLabel = {
    display:
      "block",

    fontSize:
      "12px",

    fontWeight:
      700,

    color:
      "#64748b",

    marginBottom:
      "6px",

    textTransform:
      "uppercase",

    letterSpacing:
      ".03em",
  };

  const estiloInput = {
    width:
      "100%",

    minHeight:
      "44px",

    padding:
      "9px 12px",

    border:
      "1px solid #cbd5e1",

    borderRadius:
      "9px",

    background:
      "#fff",

    color:
      "#1e293b",

    fontSize:
      "14px",

    textAlign:
      "left",

    boxSizing:
      "border-box",
  };

  const estiloTarjeta = {
    background:
      "#fff",

    border:
      "1px solid #e2e8f0",

    borderRadius:
      "14px",

    overflow:
      "hidden",

    marginBottom:
      "18px",
  };

  // ==========================================================
  // SIN DERECHO DE ADMINISTRACIÓN
  // ==========================================================

  if (
    !puedeGestionarUsuarios
  ) {
    return (
      <div className="alert alert-danger">
        No posee nivel de administrador
        para modificar usuarios.
      </div>
    );
  }

  // ==========================================================
  // USUARIO FUERA DEL ÁMBITO
  // ==========================================================

  if (
    cargandoDestinos || !usuarioDentroDelAmbito
  ) {
    return (
      <div
        style={{
          padding:
            "25px",

          background:
            "#fff",

          border:
            "1px solid #fecaca",

          borderRadius:
            "14px",

          color:
            "#991b1b",
        }}
      >
        <strong>
          {cargandoDestinos ? "Cargando organigrama..." : "⛔ Usuario fuera de su ámbito"}
        </strong>

        <div
          style={{
            marginTop:
              "8px",
          }}
        >
          {esAdministrador &&
          Number(
            parametro.nivel
          ) <= 2
            ? "Como administrador no puede modificar usuarios con nivel Super User o Administrador."
            : "Este usuario está fuera de su ámbito de administración."}
        </div>
      </div>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        width:
          "100%",

        maxWidth:
          "1050px",

        margin:
          "0 auto",

        textAlign:
          "left",
      }}
    >
      {/* ======================================================
          CABECERA
      ====================================================== */}

      <div
        style={{
          background:
            "linear-gradient(135deg,#eff6ff,#f8fafc)",

          border:
            "1px solid #dbeafe",

          borderRadius:
            "14px",

          padding:
            "18px",

          marginBottom:
            "20px",

          display:
            "flex",

          alignItems:
            "center",

          justifyContent:
            "space-between",

          gap:
            "15px",

          flexWrap:
            "wrap",
        }}
      >
        <div
          style={{
            display:
              "flex",

            gap:
              "14px",

            alignItems:
              "center",
          }}
        >
          <div
            style={{
              width:
                "54px",

              height:
                "54px",

              borderRadius:
                "50%",

              background:
                "#1e3a5f",

              color:
                "#fff",

              display:
                "flex",

              justifyContent:
                "center",

              alignItems:
                "center",

              fontSize:
                "18px",

              fontWeight:
                700,
            }}
          >
            {String(
              parametro.apellido ||
                "U"
            )
              .charAt(0)
              .toUpperCase()}

            {String(
              parametro.nombre ||
                ""
            )
              .charAt(0)
              .toUpperCase()}
          </div>

          <div>
            <div
              style={{
                color:
                  "#64748b",

                fontSize:
                  "12px",

                textTransform:
                  "uppercase",

                fontWeight:
                  700,
              }}
            >
              Usuario seleccionado
            </div>

            <div
              style={{
                color:
                  "#172033",

                fontSize:
                  "21px",

                fontWeight:
                  700,
              }}
            >
              {parametro.apellido},{" "}
              {parametro.nombre}
            </div>

            <div
              style={{
                color:
                  "#64748b",

                marginTop:
                  "2px",

                fontSize:
                  "13px",
              }}
            >
              DNI / Identificación:{" "}

              <strong>
                {
                  parametro.identificacion
                }
              </strong>
            </div>
          </div>
        </div>

        <div>
          <span
            className={
              String(
                parametro.estado
              ) === "1"
                ? "badge bg-success"
                : "badge bg-danger"
            }
            style={{
              padding:
                "8px 10px",

              marginRight:
                "6px",
            }}
          >
            {String(
              parametro.estado
            ) === "1"
              ? "Activo"
              : "Suspendido"}
          </span>

          <span
            className="badge bg-secondary"
            style={{
              padding:
                "8px 10px",
            }}
          >
            {nombreNivel(
              parametro.nivel
            )}
          </span>
        </div>
      </div>

      {/* ======================================================
          INFO ADMINISTRADOR
      ====================================================== */}

      {esAdministrador && (
        <div
          style={{
            background:
              "#eff6ff",

            border:
              "1px solid #bfdbfe",

            borderRadius:
              "12px",

            padding:
              "12px 15px",

            marginBottom:
              "18px",

            color:
              "#1e40af",

            fontSize:
              "13px",
          }}
        >
          <strong>
            Ámbito de administración:
          </strong>{" "}

          puede gestionar usuarios
          Operador/Observador dentro de su
          dependencia y todas las
          dependencias subordinadas.{" "}

          <strong>
            {
              destinosPermitidos.length
            }
          </strong>{" "}
          dependencia
          {destinosPermitidos.length !==
          1
            ? "s"
            : ""}{" "}
          disponibles.

          <br />

          Los permisos que puede delegar
          están limitados a los que posee
          su propia cuenta.
        </div>
      )}

      {/* ======================================================
          FORM
      ====================================================== */}

      <form
        onSubmit={
          handleSubmit(
            guardar
          )
        }
      >
        {/* ====================================================
            DATOS PERSONALES
        ==================================================== */}

        <div
          style={
            estiloTarjeta
          }
        >
          <div
            style={{
              padding:
                "13px 17px",

              background:
                "#f8fafc",

              borderBottom:
                "1px solid #e2e8f0",

              fontWeight:
                700,

              color:
                "#334155",
            }}
          >
            👤 Datos del usuario
          </div>

          <div
            style={{
              padding:
                "18px",

              display:
                "grid",

              gridTemplateColumns:
                "repeat(auto-fit,minmax(250px,1fr))",

              gap:
                "16px",
            }}
          >
            <div>
              <label
                style={
                  estiloLabel
                }
              >
                Apellido
              </label>

              <input
                type="text"

                style={
                  estiloInput
                }

                {...register(
                  "apellido",
                  {
                    required:
                      true,

                    minLength:
                      3,

                    maxLength:
                      32,
                  }
                )}
              />

              {errors.apellido && (
                <div
                  style={{
                    color:
                      "#dc2626",

                    fontSize:
                      "12px",
                  }}
                >
                  Apellido inválido.
                </div>
              )}
            </div>

            <div>
              <label
                style={
                  estiloLabel
                }
              >
                Nombre
              </label>

              <input
                type="text"

                style={
                  estiloInput
                }

                {...register(
                  "nombre",
                  {
                    required:
                      true,

                    minLength:
                      3,

                    maxLength:
                      32,
                  }
                )}
              />

              {errors.nombre && (
                <div
                  style={{
                    color:
                      "#dc2626",

                    fontSize:
                      "12px",
                  }}
                >
                  Nombre inválido.
                </div>
              )}
            </div>

            {esSuperUsuario && (
              <div>
                <label
                  style={
                    estiloLabel
                  }
                >
                  Nº de identificación
                </label>

                <input
                  type="text"

                  style={
                    estiloInput
                  }

                  {...register(
                    "identificacion",
                    {
                      required:
                        true,

                      minLength:
                        3,

                      maxLength:
                        16,
                    }
                  )}
                />
              </div>
            )}
          </div>
        </div>

        {/* ====================================================
            UBICACIÓN
        ==================================================== */}

        <div
          style={
            estiloTarjeta
          }
        >
          <div
            style={{
              padding:
                "13px 17px",

              background:
                "#f8fafc",

              borderBottom:
                "1px solid #e2e8f0",

              fontWeight:
                700,

              color:
                "#334155",
            }}
          >
            🏢 Ubicación institucional
          </div>

          <div
            style={{
              padding:
                "18px",

              display:
                "grid",

              gridTemplateColumns:
                "repeat(auto-fit,minmax(280px,1fr))",

              gap:
                "16px",
            }}
          >
            {/* SEDE */}

            <div>
              <label
                style={
                  estiloLabel
                }
              >
                Sede de destino
              </label>

              <select
                style={
                  estiloInput
                }

                {...register(
                  "sedeID",
                  {
                    required:
                      true,
                  }
                )}
              >
                {sedesPermitidas.map(
                  (sede) => (
                    <option
                      key={
                        sede.id
                      }

                      value={
                        sede.id
                      }
                    >
                      {
                        sede.descripcion
                      }
                    </option>
                  )
                )}
              </select>

              {sedeActual && (
                <div
                  style={{
                    color:
                      "#64748b",

                    fontSize:
                      "12px",

                    marginTop:
                      "5px",
                  }}
                >
                  Seleccionada:{" "}

                  <strong>
                    {
                      sedeActual.descripcion
                    }
                  </strong>
                </div>
              )}
            </div>

            {/* DESTINO */}

            <div>
              <label
                style={
                  estiloLabel
                }
              >
                Unidad de destino
              </label>

              <select
                style={
                  estiloInput
                }

                {...register(
                  "destino",
                  {
                    required:
                      true,
                  }
                )}
              >
                {destinosEditables.map(
                  (
                    destino
                  ) => (
                    <option
                      key={
                        destino.id
                      }

                      value={
                        destino.id
                      }
                    >
                      {
                        destino.descripcion
                      }
                    </option>
                  )
                )}
              </select>

              {destinoActual && (
                <div
                  style={{
                    color:
                      "#64748b",

                    fontSize:
                      "12px",

                    marginTop:
                      "5px",
                  }}
                >
                  Seleccionado:{" "}

                  <strong>
                    {
                      destinoActual.descripcion
                    }
                  </strong>
                </div>
              )}

              {esAdministrador && (
                <div
                  style={{
                    marginTop:
                      "7px",

                    color:
                      "#1d4ed8",

                    fontSize:
                      "12px",
                  }}
                >
                  Solo aparecen los destinos autorizados para su cuenta.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ====================================================
            ACCESO
        ==================================================== */}

        <div
          style={
            estiloTarjeta
          }
        >
          <div
            style={{
              padding:
                "13px 17px",

              background:
                "#f8fafc",

              borderBottom:
                "1px solid #e2e8f0",

              fontWeight:
                700,

              color:
                "#334155",
            }}
          >
            🔐 Acceso al sistema
          </div>

          <div
            style={{
              padding:
                "18px",

              display:
                "grid",

              gridTemplateColumns:
                "repeat(auto-fit,minmax(250px,1fr))",

              gap:
                "16px",
            }}
          >
            {/* NIVEL */}

            <div>
              <label
                style={
                  estiloLabel
                }
              >
                Nivel de acceso
              </label>

              <select
                style={
                  estiloInput
                }

                {...register(
                  "nivel",
                  {
                    required:
                      true,
                  }
                )}
              >
                {esSuperUsuario && (
                  <>
                    <option value="1">
                      Super User
                    </option>

                    <option value="2">
                      Administrador
                    </option>
                  </>
                )}

                <option value="3">
                  Operador
                </option>

                <option value="4">
                  Observador
                </option>
              </select>

              <div
                style={{
                  marginTop:
                    "6px",

                  fontSize:
                    "12px",

                  color:
                    "#64748b",
                }}
              >
                Seleccionado:{" "}

                <strong>
                  {nombreNivel(
                    nivelActual
                  )}
                </strong>
              </div>

              {esAdministrador && (
                <div
                  style={{
                    color:
                      "#1d4ed8",

                    fontSize:
                      "12px",

                    marginTop:
                      "4px",
                  }}
                >
                  Como administrador puede
                  asignar solamente Operador
                  u Observador.
                </div>
              )}
            </div>

            {/* ESTADO */}

            <div>
              <label
                style={
                  estiloLabel
                }
              >
                Estado
              </label>

              <select
                style={
                  estiloInput
                }

                {...register(
                  "estado",
                  {
                    required:
                      true,
                  }
                )}
              >
                <option value="1">
                  Activo
                </option>

                <option value="2">
                  Suspendido
                </option>
              </select>

              <div
                style={{
                  marginTop:
                    "6px",

                  fontSize:
                    "12px",

                  color:
                    estadoActual ===
                    "Activo"
                      ? "#047857"
                      : "#b45309",

                  fontWeight:
                    600,
                }}
              >
                ● {estadoActual}
              </div>
            </div>
          </div>
        </div>

        {/* ====================================================
            PERMISOS
        ==================================================== */}

        <div
          style={
            estiloTarjeta
          }
        >
          <div
            style={{
              padding:
                "13px 17px",

              background:
                "#f8fafc",

              display:
                "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              gap:
                "10px",

              borderBottom:
                mostrarPermisos
                  ? "1px solid #e2e8f0"
                  : "none",

              cursor:
                "pointer",
            }}

            onClick={() =>
              setMostrarPermisos(
                !mostrarPermisos
              )
            }
          >
            <div>
              <div
                style={{
                  fontWeight:
                    700,

                  color:
                    "#334155",
                }}
              >
                🛡️ Permisos del usuario
              </div>

              <div
                style={{
                  color:
                    "#64748b",

                  fontSize:
                    "12px",

                  marginTop:
                    "2px",
                }}
              >
                {esSuperUsuario
                  ? "Puede administrar todos los permisos."
                  : `Puede delegar ${permisosAdministrador.length} permisos de su propia cuenta.`}
              </div>
            </div>

            <button
              type="button"

              className="btn btn-outline-secondary btn-sm"

              onClick={(
                e
              ) => {
                e.stopPropagation();

                setMostrarPermisos(
                  !mostrarPermisos
                );
              }}
            >
              {mostrarPermisos
                ? "Ocultar ▲"
                : "Gestionar ▼"}
            </button>
          </div>

          {mostrarPermisos && (
            <div
              style={{
                padding:
                  "18px",

                background:
                  "#fff",
              }}
            >
              <Permisos
                value={
                  permisos
                }

                permisosPermitidos={
                  permisosAdministrador
                }

                esSuperUsuario={
                  esSuperUsuario
                }

                permisosProtegidos={
                  permisosProtegidos
                }

                onChange={
                  cambiarPermisos
                }
              />
            </div>
          )}
        </div>

        {/* ====================================================
            GUARDAR
        ==================================================== */}

        <div
          style={{
            position:
              "sticky",

            bottom:
              0,

            zIndex:
              5,

            background:
              "#f8fafc",

            border:
              "1px solid #e2e8f0",

            borderRadius:
              "12px",

            padding:
              "14px",

            display:
              "flex",

            justifyContent:
              "space-between",

            alignItems:
              "center",

            gap:
              "12px",

            flexWrap:
              "wrap",

            boxShadow:
              "0 -4px 15px rgba(15,23,42,.05)",
          }}
        >
          <div
            style={{
              fontSize:
                "13px",

              color:
                "#64748b",
            }}
          >
            {isDirty
              ? "● Hay cambios pendientes"
              : "Puede guardar cambios de permisos aunque los datos principales no hayan cambiado"}
          </div>

          <button
            type="submit"

            className="btn btn-primary"

            disabled={
              guardando
            }

            style={{
              minWidth:
                "160px",
            }}
          >
            {guardando
              ? "Guardando..."
              : "💾 Guardar cambios"}
          </button>
        </div>
      </form>

      {/* ======================================================
          ACCIONES ADMINISTRATIVAS
      ====================================================== */}

      <div
        style={{
          marginTop:
            "22px",

          border:
            "1px solid #fecaca",

          borderRadius:
            "14px",

          overflow:
            "hidden",

          background:
            "#fff",
        }}
      >
        <div
          style={{
            padding:
              "13px 17px",

            background:
              "#fff7ed",

            borderBottom:
              "1px solid #fed7aa",

            fontWeight:
              700,

            color:
              "#9a3412",
          }}
        >
          ⚠️ Acciones administrativas
        </div>

        <div
          style={{
            padding:
              "18px",

            display:
              "grid",

            gridTemplateColumns:
              "repeat(auto-fit,minmax(260px,1fr))",

            gap:
              "14px",
          }}
        >
          <div
            style={{
              border:
                "1px solid #fecaca",

              borderRadius:
                "11px",

              padding:
                "15px",
            }}
          >
            <strong>
              🔑 Blanquear contraseña
            </strong>

            <div
              style={{
                fontSize:
                  "12px",

                color:
                  "#64748b",

                margin:
                  "7px 0 12px",
              }}
            >
              Restablece la contraseña a{" "}
              <strong>
                Alem+2050
              </strong>.
            </div>

            <button
              type="button"

              className="btn btn-outline-danger w-100"

              onClick={
                blanquear
              }

              disabled={
                blanqueando
              }
            >
              {blanqueando
                ? "Procesando..."
                : "Blanquear contraseña"}
            </button>
          </div>

          <div
            style={{
              border:
                "1px solid #fde68a",

              borderRadius:
                "11px",

              padding:
                "15px",
            }}
          >
            <strong>
              🚪 Cerrar sesión
            </strong>

            <div
              style={{
                fontSize:
                  "12px",

                color:
                  "#64748b",

                margin:
                  "7px 0 12px",
              }}
            >
              Fuerza el cierre de la sesión
              activa mediante socket.
            </div>

            <button
              type="button"

              className="btn btn-outline-warning w-100"

              onClick={
                CierraSesion
              }

              disabled={
                cerrandoSesion
              }
            >
              {cerrandoSesion
                ? "Cerrando..."
                : "Cerrar sesión del usuario"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModuloEditaUsuarios;